import {
  type AccountSelectorContextProviderEntityQuery,
  type OrganizationSubscriptionBasicInfoByNameQuery,
  useAccountSelectorContextProviderEntityQuery,
  useOrganizationSubscriptionBasicInfoByNameQuery,
  useViewerUserInfoQuery,
  type ViewerUserInfoQuery,
} from '../../../generated/graphql';
import {isProfilePath, useCurrentRouteMatch} from '../../../routes/utils';
import {NetworkStatus} from '../../../state/graphql/apolloWithAbort';
import {useViewer} from '../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../util/admin';
import {useRampFlagAccountSelector} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {isLegacyPersonalEntity} from './AccountSelectorUtils';
import {type Account} from './types';
import {useAccountList} from './useAccountList';
import {useLegacyPersonalEntityOwner} from './useLegacyPersonalEntityOwner';

type MatchParams = {
  params: {
    entityName?: string;
    orgName?: string;
  };
};

export type UrlAccountInputs = {
  viewer: ReturnType<typeof useViewer>;
  accountList: Account[];
  isAccountListLoading: boolean;
  userInfo: NonNullable<ViewerUserInfoQuery['viewer']>['userInfo'] | undefined;
  orgName: string | undefined;
  entityName: string | undefined;
  orgQuery: OrganizationSubscriptionBasicInfoByNameQuery | undefined;
  entityQuery: AccountSelectorContextProviderEntityQuery | undefined;
  personalEntityUserID: string | undefined;
  isAdminMode: boolean;
  isProfilePath: boolean;
  isLoaded: boolean;
};

// The shared resolution inputs for which account the current URL names:
// route params, entity/org queries, viewer, account list, admin mode, and
// the ramp flag. Both account-selector wrappers consume the same block.
export function useUrlAccountInputs(): UrlAccountInputs {
  const viewer = useViewer();
  const {accountList, isLoading: isAccountListLoading} = useAccountList();
  const {data: viewerData, loading: userInfoLoading} = useViewerUserInfoQuery();
  const {
    params: {orgName, entityName},
  }: MatchParams = useCurrentRouteMatch() ?? {params: {}};
  const {data: entityOrg, networkStatus: entityNetworkStatus} =
    useAccountSelectorContextProviderEntityQuery({
      variables: {teamName: entityName ?? ''},
      skip: !entityName,
    });

  // The organization associated with the orgName in the URL gets precedence over the
  // entity's org (in the event that they don't match).
  const orgNameFromCurrentUrl =
    orgName ?? entityOrg?.entity?.organization?.name;
  const skipOrgQuery = !orgNameFromCurrentUrl;
  const {data: org, networkStatus} =
    useOrganizationSubscriptionBasicInfoByNameQuery({
      variables: {name: orgNameFromCurrentUrl ?? ''},
      skip: skipOrgQuery,
    });
  const enableAccountSelector = useRampFlagAccountSelector();
  const isAdminMode = useAdminModeActive();
  const isCurrentPathProfile = isProfilePath(window.location.pathname);
  const isViewerPersonalEntity =
    viewer?.username != null &&
    (entityName === viewer.username || orgName === viewer.username);
  const isOrgQueryReady = skipOrgQuery || networkStatus === NetworkStatus.ready;
  // Only admin routes to a foreign legacy personal entity need its owner ID.
  // Keep this compatibility lookup off the normal account-selector load path;
  // remove it when supported deployments no longer return personal entities.
  const shouldLoadLegacyPersonalEntityOwner =
    enableAccountSelector &&
    isAdminMode &&
    entityName != null &&
    !isViewerPersonalEntity &&
    isLegacyPersonalEntity(entityOrg?.entity) &&
    entityOrg?.entity?.organization == null &&
    isOrgQueryReady &&
    org?.organization == null;
  const {
    ownerID: legacyPersonalEntityOwnerID,
    isLoading: isLegacyPersonalEntityOwnerLoading,
  } = useLegacyPersonalEntityOwner({
    entityName,
    shouldLoad: shouldLoadLegacyPersonalEntityOwner,
  });

  const loading =
    isAccountListLoading ||
    viewer == null ||
    userInfoLoading ||
    (entityName && entityNetworkStatus !== NetworkStatus.ready) ||
    (!skipOrgQuery && networkStatus !== NetworkStatus.ready) ||
    isLegacyPersonalEntityOwnerLoading;

  const isLoaded = !loading && enableAccountSelector;

  return {
    viewer,
    accountList,
    isAccountListLoading,
    userInfo: viewerData?.viewer?.userInfo,
    orgName,
    entityName,
    orgQuery: org,
    entityQuery: entityOrg,
    personalEntityUserID: legacyPersonalEntityOwnerID,
    isAdminMode,
    isProfilePath: isCurrentPathProfile,
    isLoaded,
  };
}
